import { DEMO_IDS, createNotification } from "../db/schema";
import { ensureJourneyDb, insertRow } from "../db";
import {
  THEHUB_EVENT,
  THEHUB_PREFIX,
  canUseTheHub,
  eventCategoryForType,
} from "./constants.js";
import { SEED_HUB_EVENTS, SEED_HUB_GROUPS, SEED_HUB_MEMBERS, SEED_HUB_POSTS } from "./seed.js";
import {
  FEED_CATALOG,
  enrichListingFields,
  feedDayStamp,
  hubListingHorizon,
  materializeCatalogRow,
  reconcileListingRow,
  resolveListingTitleLink,
  sanitizeListingUrl,
  sourceLabel,
  sourceMeta,
} from "./feeds.js";
import {
  formatHubListingDate,
  hubContactLooksPlaceholder,
  hubListingPhoneLooksValid,
  sanitizeHubText,
} from "./format.js";
import { bumpEventStat, ensureFounderHub, getFounderSettings, isKnownHubRoom, listAllHubRooms } from "./founder.js";

const KEYS = {
  posts: `${THEHUB_PREFIX}posts`,
  events: `${THEHUB_PREFIX}events`,
  groups: `${THEHUB_PREFIX}groups`,
  members: `${THEHUB_PREFIX}members`,
  requests: `${THEHUB_PREFIX}requests`,
  blocks: `${THEHUB_PREFIX}blocks`,
  reads: `${THEHUB_PREFIX}reads`,
  feedDay: `${THEHUB_PREFIX}feed_day`,
  feedAt: `${THEHUB_PREFIX}feed_at`,
  listingRsvp: `${THEHUB_PREFIX}listing_rsvp`,
};

const RATE_WINDOW_MS = 8 * 1000;
const RATE_BURST = 6;
const RATE_BURST_MS = 5 * 60 * 1000;

let listingTemplateIndex = null;

function hubListingTemplates() {
  if (!listingTemplateIndex) {
    listingTemplateIndex = new Map();
    for (const row of [...SEED_HUB_EVENTS, ...FEED_CATALOG]) {
      if (!row?.id) continue;
      listingTemplateIndex.set(row.id, enrichListingFields({ ...row }, null));
    }
  }
  return listingTemplateIndex;
}

function enrichStoredListing(row) {
  return enrichListingFields(row, hubListingTemplates());
}

function finalizeListingRow(row, today) {
  const reconciled = reconcileListingRow(row, today);
  return reconciled ? enrichStoredListing(reconciled) : null;
}

function listingNeedsDetailRefresh(row) {
  const enriched = enrichStoredListing(row);
  const hadBadUrl = sanitizeListingUrl(row.url) !== String(row.url || "").trim();
  const sourceHome = sanitizeListingUrl(sourceMeta(row.source_id)?.url || "");
  const urlWasOnlyHostHome =
    sourceHome &&
    sanitizeListingUrl(row.url) &&
    sanitizeListingUrl(row.url).replace(/\/$/, "") === sourceHome.replace(/\/$/, "");
  const contactClean = String(row.contact || "").trim();
  const phoneClean = String(row.phone || "").trim();
  return (
    hadBadUrl ||
    urlWasOnlyHostHome ||
    hubContactLooksPlaceholder(row.contact) ||
    hubContactLooksPlaceholder(row.phone) ||
    hubContactLooksPlaceholder(row.address) ||
    (enriched.url || "") !== String(row.url || "").trim() ||
    (enriched.address || "") !== String(row.address || "").trim() ||
    (enriched.contact || "") !== contactClean ||
    (enriched.phone || "") !== phoneClean ||
    (!hubListingPhoneLooksValid(row.phone) && hubListingPhoneLooksValid(enriched.phone)) ||
    /\.example\b/i.test(String(row.contact || ""))
  );
}

function read(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (raw == null) return fallback;
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function write(key, value) {
  localStorage.setItem(key, JSON.stringify(value));
  emitTheHubChange();
}

function nowIso() {
  return new Date().toISOString();
}

function newId() {
  return crypto.randomUUID();
}

function mergeSeed(key, seed) {
  const stored = read(key, null);
  if (!Array.isArray(stored) || !stored.length) {
    write(key, seed);
    return seed;
  }
  const have = new Set(stored.map((item) => item.id).filter(Boolean));
  const extra = seed.filter((item) => item.id && !have.has(item.id));
  if (!extra.length) return stored;
  const next = [...stored, ...extra];
  write(key, next);
  return next;
}

export function emitTheHubChange() {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(`${THEHUB_PREFIX}revision`, String(Date.now()));
  } catch {
    /* ignore */
  }
  window.dispatchEvent(new Event(THEHUB_EVENT));
}

export function isTheHubStorageKey(key) {
  return Boolean(key && String(key).startsWith(THEHUB_PREFIX));
}

function refreshDailyFeeds(force = false) {
  ensureFounderHub();
  const settings = getFounderSettings();
  const hours = Math.max(1, Number(settings.intervalHours) || 24);
  const lastAt = Number(read(KEYS.feedAt, 0)) || 0;
  const today = feedDayStamp();
  const horizon = hubListingHorizon(today);
  const lastFeedDay = read(KEYS.feedDay, "") || "";
  const dayChanged = Boolean(lastFeedDay && lastFeedDay !== today);
  const stored = read(KEYS.events, []);
  const hasStale = stored.some((row) => {
    if (!row.date) return true;
    return row.date < today || row.date > horizon;
  });
  const needsDetails = stored.some((row) => !row.hidden && listingNeedsDetailRefresh(row));
  if (!force && !dayChanged && !hasStale && !needsDetails && lastAt && Date.now() - lastAt < hours * 3600 * 1000)
    return;

  const off = new Set(settings.offSources || []);
  const have = new Set(stored.map((row) => row.id).filter(Boolean));
  const incoming = FEED_CATALOG.filter(
    (row) => row.id && !have.has(row.id) && !off.has(row.source_id)
  )
    .map((row) => materializeCatalogRow(row, today))
    .filter(Boolean);
  const next = [...stored, ...incoming]
    .map((row) => finalizeListingRow(row, today))
    .filter(Boolean)
    .map((row) => {
      const sourceHome = sanitizeListingUrl(sourceMeta(row.source_id)?.url || "");
      const listingUrl = sanitizeListingUrl(row.url);
      if (
        sourceHome &&
        listingUrl &&
        listingUrl.replace(/\/$/, "") === sourceHome.replace(/\/$/, "")
      ) {
        return { ...row, url: "" };
      }
      return row;
    });
  localStorage.setItem(KEYS.events, JSON.stringify(next));
  localStorage.setItem(KEYS.feedDay, JSON.stringify(today));
  localStorage.setItem(KEYS.feedAt, JSON.stringify(Date.now()));
  emitTheHubChange();
}

export function forceRefreshHubFeeds() {
  localStorage.setItem(KEYS.feedAt, JSON.stringify(0));
  refreshDailyFeeds(true);
}

export function lastHubFeedDay() {
  ensureTheHub();
  return read(KEYS.feedDay, "") || feedDayStamp();
}

export function lastHubFeedAt() {
  ensureTheHub();
  return Number(read(KEYS.feedAt, 0)) || 0;
}

export function listHubSources() {
  return FEED_CATALOG.map((row) => sourceLabel(row.source_id)).filter(Boolean);
}

export function ensureTheHub() {
  mergeSeed(KEYS.events, SEED_HUB_EVENTS);
  mergeSeed(KEYS.posts, SEED_HUB_POSTS);
  mergeSeed(KEYS.groups, SEED_HUB_GROUPS);
  mergeSeed(KEYS.members, SEED_HUB_MEMBERS);
  if (read(KEYS.requests, null) == null) write(KEYS.requests, []);
  if (read(KEYS.blocks, null) == null) write(KEYS.blocks, []);
  if (read(KEYS.reads, null) == null) write(KEYS.reads, {});
  ensureFounderHub();
  refreshDailyFeeds();
  return true;
}

export function assertHubAccess(session) {
  if (!canUseTheHub(session) || !session?.userId) {
    throw new Error("The Hub is for signed-in brokers and agents.");
  }
  if (isHubBlocked(session.userId) && session.role !== "admin") {
    throw new Error("Your Hub access is paused. Please write to support if this is a surprise.");
  }
}

export function isHubBlocked(userId) {
  ensureTheHub();
  return read(KEYS.blocks, []).some((row) => row.user_id === userId);
}

export function listHubBlocks() {
  ensureTheHub();
  return read(KEYS.blocks, []);
}

export function blockHubUser(userId, session, reason = "") {
  assertHubAccess(session);
  if (session.role !== "admin") throw new Error("Only the program desk can pause a member.");
  if (!userId || userId === session.userId) throw new Error("That member cannot be paused.");
  const rows = read(KEYS.blocks, []);
  if (rows.some((row) => row.user_id === userId)) return rows;
  const next = [...rows, { user_id: userId, at: nowIso(), by: session.userId, reason }];
  write(KEYS.blocks, next);
  return next;
}

export function unblockHubUser(userId, session) {
  assertHubAccess(session);
  if (session.role !== "admin") throw new Error("Only the program desk can restore a member.");
  write(
    KEYS.blocks,
    read(KEYS.blocks, []).filter((row) => row.user_id !== userId)
  );
}

function recentPostsByAuthor(userId) {
  return read(KEYS.posts, []).filter(
    (row) => row.author_id === userId && !row.hidden && Date.parse(row.created_at) > Date.now() - RATE_BURST_MS
  );
}

export function checkRateLimit(userId) {
  const mine = recentPostsByAuthor(userId).sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)));
  if (mine.length >= RATE_BURST) {
    throw new Error("Please pause a little — The Hub stays useful when it stays unhurried.");
  }
  const last = mine[0];
  if (last && Date.now() - Date.parse(last.created_at) < RATE_WINDOW_MS) {
    throw new Error("Please wait a moment before posting again.");
  }
}

export function listHubPosts({ category, groupId = null, includeHidden = false } = {}) {
  ensureTheHub();
  return read(KEYS.posts, [])
    .filter((row) => (includeHidden ? true : !row.hidden))
    .filter((row) => !category || row.category === category)
    .filter((row) => (groupId ? row.group_id === groupId : !row.group_id))
    .sort((a, b) => {
      if (Boolean(a.pinned) !== Boolean(b.pinned)) return a.pinned ? -1 : 1;
      return String(a.created_at).localeCompare(String(b.created_at));
    });
}

export function postHubMessage({ session, category, groupId = null, parentId = null, body, kind = "message" }) {
  assertHubAccess(session);
  checkRateLimit(session.userId);
  const text = sanitizeHubText(body);
  if (!text) throw new Error("Write a short note first.");
  if (!isKnownHubRoom(category)) throw new Error("Unknown room.");
  if (kind === "announcement" && session.role !== "broker" && session.role !== "admin") {
    throw new Error("Announcements are for brokers and the program desk.");
  }
  if (kind === "prompt") throw new Error("Those threads are already open in this room.");
  if (groupId) {
    if (session.role !== "admin" && !isGroupMember(groupId, session.userId)) {
      throw new Error("Join this circle first.");
    }
  }
  if (parentId) {
    const parent = read(KEYS.posts, []).find((row) => row.id === parentId);
    if (!parent || parent.hidden) throw new Error("That conversation is no longer open.");
  }
  const row = {
    id: newId(),
    category,
    group_id: groupId || null,
    parent_id: parentId || null,
    kind: kind === "announcement" ? "announcement" : "message",
    body: text,
    author_id: session.userId,
    author_name: session.name || session.email || "Member",
    author_role: session.role,
    created_at: nowIso(),
    pinned: false,
    hidden: false,
  };
  write(KEYS.posts, [...read(KEYS.posts, []), row]);
  return row;
}

export function hideHubPost(id, session) {
  assertHubAccess(session);
  if (session.role !== "admin") throw new Error("Only the program desk can remove a note.");
  write(
    KEYS.posts,
    read(KEYS.posts, []).map((row) => (row.id === id ? { ...row, hidden: true } : row))
  );
}

export function pinHubPost(id, session, pinned = true) {
  assertHubAccess(session);
  if (session.role !== "admin") throw new Error("Only the program desk can pin a note.");
  write(
    KEYS.posts,
    read(KEYS.posts, []).map((row) => (row.id === id ? { ...row, pinned: Boolean(pinned) } : row))
  );
}

export function listHubEvents({ category, specialOnly = false, groupId = null } = {}) {
  ensureTheHub();
  const today = feedDayStamp();
  const horizon = hubListingHorizon(today);
  return read(KEYS.events, [])
    .map((row) => finalizeListingRow(row, today))
    .filter(Boolean)
    .filter((row) => !row.hidden)
    .filter((row) => (specialOnly ? row.special : true))
    .filter((row) => row.date >= today && row.date <= horizon)
    .filter((row) => {
      if (groupId) return row.group_id === groupId;
      return !row.group_id;
    })
    .filter((row) => !category || row.category === category || (category === "special" && row.special))
    .sort((a, b) => {
      if (Boolean(a.featured) !== Boolean(b.featured)) return a.featured ? -1 : 1;
      return String(a.date).localeCompare(String(b.date)) || String(a.time).localeCompare(String(b.time));
    });
}

export function listSpecialEvents() {
  return listHubEvents({ specialOnly: true });
}

export function saveHubEvent(input, session) {
  assertHubAccess(session);
  const groupId = input.group_id || null;
  if (session.role === "broker") {
    if (!groupId || input.special) {
      throw new Error("Brokers add listings inside their own office circle.");
    }
    const group = listHubGroups().find((row) => row.id === groupId);
    if (!group || group.brokerage_id !== session.userId) {
      throw new Error("This circle belongs to another office.");
    }
  } else if (session.role !== "admin") {
    throw new Error("Only the program desk can edit listings.");
  }
  const type = input.type || "other";
  const row = {
    id: input.id || newId(),
    title: String(input.title || "").trim(),
    type,
    category: groupId ? "associations" : input.special ? "special" : eventCategoryForType(type),
    group_id: groupId,
    date: input.date || "",
    time: input.time || "",
    location: String(input.location || "").trim(),
    address: String(input.address || "").trim(),
    contact: String(input.contact || "").trim(),
    phone: String(input.phone || "").trim(),
    description: sanitizeHubText(input.description || ""),
    url: String(input.url || "").trim(),
    host: String(input.host || "").trim(),
    special: Boolean(input.special) && !groupId,
    featured: Boolean(input.featured),
    curated: Boolean(input.curated),
    source_id: String(input.source_id || "").trim(),
    thumbnail: String(input.thumbnail || "").trim(),
    rolling: Boolean(input.rolling),
    roll_offset: input.roll_offset || null,
    created_at: input.created_at || nowIso(),
    hidden: false,
  };
  if (!row.title || !row.date) throw new Error("A title and date are needed.");
  const rows = read(KEYS.events, []);
  const index = rows.findIndex((item) => item.id === row.id);
  const merged = index === -1 ? row : { ...rows[index], ...row };
  const saved = enrichStoredListing(merged);
  if (index === -1) rows.push(saved);
  else rows[index] = saved;
  write(KEYS.events, rows);
  return saved;
}

function canManageEvent(row, session) {
  if (session.role === "admin") return true;
  if (session.role !== "broker" || !row?.group_id) return false;
  const group = listHubGroups().find((item) => item.id === row.group_id);
  return Boolean(group && group.brokerage_id === session.userId);
}

export function hideHubEvent(id, session) {
  assertHubAccess(session);
  const current = read(KEYS.events, []).find((row) => row.id === id);
  if (!canManageEvent(current, session)) {
    throw new Error("Only the program desk can remove a listing.");
  }
  write(
    KEYS.events,
    read(KEYS.events, []).map((row) => (row.id === id ? { ...row, hidden: true } : row))
  );
}

export function listHubGroups() {
  ensureTheHub();
  return read(KEYS.groups, []).sort((a, b) => a.name.localeCompare(b.name));
}

export function membersOfGroup(groupId) {
  ensureTheHub();
  return read(KEYS.members, []).filter((row) => row.group_id === groupId);
}

export function isGroupMember(groupId, userId) {
  return membersOfGroup(groupId).some((row) => row.user_id === userId);
}

export function createHubGroup({ session, name, summary }) {
  assertHubAccess(session);
  if (session.role !== "broker" && session.role !== "admin") {
    throw new Error("Brokers create association circles for their office.");
  }
  const title = String(name || "").trim();
  if (!title) throw new Error("Give the circle a name.");
  const brokerageId = session.role === "broker" ? session.userId : session.brokerageId || session.userId;
  const row = {
    id: newId(),
    name: title,
    summary: sanitizeHubText(summary || ""),
    brokerage_id: brokerageId,
    created_by: session.userId,
    created_at: nowIso(),
  };
  write(KEYS.groups, [...read(KEYS.groups, []), row]);
  write(KEYS.members, [
    ...read(KEYS.members, []),
    { group_id: row.id, user_id: session.userId, joined_at: nowIso() },
  ]);
  return row;
}

export function joinHubGroup({ session, groupId }) {
  assertHubAccess(session);
  const group = listHubGroups().find((row) => row.id === groupId);
  if (!group) throw new Error("That circle was not found.");
  if (session.role === "agent" && group.brokerage_id && session.brokerageId && group.brokerage_id !== session.brokerageId) {
    throw new Error("This circle belongs to another office.");
  }
  if (isGroupMember(groupId, session.userId)) return group;
  write(KEYS.members, [
    ...read(KEYS.members, []),
    { group_id: groupId, user_id: session.userId, joined_at: nowIso() },
  ]);
  return group;
}

export function submitEventRequest(form, session) {
  assertHubAccess(session);
  const title = String(form.title || "").trim();
  if (!title) throw new Error("Please include an event title.");
  const row = {
    id: newId(),
    status: "pending",
    mode: form.mode === "correction" ? "correction" : "request",
    title,
    type: form.type || "other",
    date: form.date || "",
    time: form.time || "",
    location: String(form.location || "").trim(),
    address: String(form.address || "").trim(),
    host: String(form.host || "").trim(),
    url: String(form.url || "").trim(),
    description: sanitizeHubText(form.description || ""),
    why: sanitizeHubText(form.why || ""),
    contact: String(form.contact || "").trim(),
    thumbnail: String(form.thumbnail || "").trim(),
    room_tag: eventCategoryForType(form.type || "other"),
    from_id: session.userId,
    from_name: session.name || session.email || "",
    from_role: session.role,
    created_at: nowIso(),
  };
  write(KEYS.requests, [...read(KEYS.requests, []), row]);
  notifyAdmins({
    kind: "hub.event_request",
    request_id: row.id,
    title: row.title,
    mode: row.mode,
  });
  return row;
}

export function listEventRequests() {
  ensureTheHub();
  return read(KEYS.requests, []).sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)));
}

export function decideEventRequest(id, status, session) {
  assertHubAccess(session);
  if (session.role !== "admin") throw new Error("Only the program desk can review requests.");
  const rows = read(KEYS.requests, []);
  const current = rows.find((row) => row.id === id);
  if (!current) throw new Error("Request not found.");
  const nextStatus = status === "approved" ? "approved" : "declined";
  write(
    KEYS.requests,
    rows.map((row) => (row.id === id ? { ...row, status: nextStatus, decided_at: nowIso() } : row))
  );
  if (nextStatus === "approved" && current.mode !== "correction") {
    saveHubEvent(
      {
        title: current.title,
        type: current.type,
        date: current.date,
        time: current.time,
        location: current.location,
        address: current.address,
        contact: current.contact,
        phone: current.phone || "",
        description: current.description,
        url: current.url,
        host: current.host,
        special: false,
        curated: false,
        thumbnail: current.thumbnail || "",
        featured: false,
      },
      session
    );
  }
  return nextStatus;
}

function notifyAdmins(payload) {
  try {
    ensureJourneyDb();
    insertRow(
      "notifications",
      createNotification({
        user_id: DEMO_IDS.admin,
        type: "system",
        payload,
      })
    );
  } catch {
    /* notifications are best-effort in this prototype */
  }
}

export function markHubRead(session, roomKey, latestIso = "") {
  if (!session?.userId || !roomKey) return;
  ensureTheHub();
  const reads = read(KEYS.reads, {});
  const user = reads[session.userId] || {};
  if (user[roomKey] && (!latestIso || user[roomKey] >= latestIso)) return;
  user[roomKey] = nowIso();
  reads[session.userId] = user;
  write(KEYS.reads, reads);
}

export function unreadInRoom(session, roomKey, posts) {
  if (!session?.userId) return 0;
  ensureTheHub();
  const seen = read(KEYS.reads, {})[session.userId]?.[roomKey];
  return posts.filter((row) => row.author_id !== session.userId && (!seen || String(row.created_at) > seen)).length;
}

export function unreadByCategory(session) {
  ensureTheHub();
  const counts = {};
  listAllHubRooms()
    .filter((cat) => !cat.archived)
    .forEach((cat) => {
      const posts = listHubPosts({ category: cat.id, groupId: null });
      counts[cat.id] = unreadInRoom(session, `cat:${cat.id}`, posts);
    });
  listHubGroups().forEach((group) => {
    const posts = listHubPosts({ category: "associations", groupId: group.id });
    counts[`group:${group.id}`] = unreadInRoom(session, `group:${group.id}`, posts);
  });
  return counts;
}

export function totalHubUnread(session) {
  return Object.values(unreadByCategory(session)).reduce((sum, n) => sum + n, 0);
}

export function hubAttendingPromptId(category) {
  return `th-prompt-${category}-attending`;
}

export function listingRoomCategory(item) {
  if (item?.group_id) return "associations";
  return item?.category || "chat";
}

export function getHubListingRsvp(userId, eventId) {
  if (!userId || !eventId) return "";
  ensureTheHub();
  const row = read(KEYS.listingRsvp, {})[`${userId}:${eventId}`];
  return row?.status === "attending" || row?.status === "interested" ? row.status : "";
}

export function respondHubListing(session, item, status) {
  assertHubAccess(session);
  if (!item?.id || (status !== "interested" && status !== "attending")) {
    return { status: "", posted: false, message: "", scrollTarget: "" };
  }
  bumpEventStat(item.id, status === "attending" ? "attending" : "interested");

  const map = read(KEYS.listingRsvp, {});
  const key = `${session.userId}:${item.id}`;
  const prev = map[key]?.status;
  map[key] = { status, at: nowIso(), event_id: item.id, user_id: session.userId };
  write(KEYS.listingRsvp, map);

  const when = [formatHubListingDate(item.date), item.time].filter(Boolean).join(" ");
  const body =
    status === "attending"
      ? `I'll attend “${item.title}”${when ? ` — ${when}` : ""}.`
      : `Interested in “${item.title}”${when ? ` (${when})` : ""}.`;

  let posted = false;
  let scrollTarget = "";
  if (item.group_id) {
    const dupe = read(KEYS.posts, []).some(
      (row) => row.group_id === item.group_id && row.author_id === session.userId && row.body === body
    );
    if (!dupe) {
      postHubMessage({
        session,
        category: "associations",
        groupId: item.group_id,
        parentId: null,
        body,
      });
      posted = true;
    }
    scrollTarget = "hub-room-conversation";
  } else {
    const category = listingRoomCategory(item);
    const promptId = hubAttendingPromptId(category);
    const prompt = read(KEYS.posts, []).find((row) => row.id === promptId && !row.hidden);
    scrollTarget = prompt ? `hub-thread-${promptId}` : "";
    if (prompt) {
      const dupe = read(KEYS.posts, []).some(
        (row) => row.parent_id === promptId && row.author_id === session.userId && row.body === body
      );
      if (!dupe) {
        postHubMessage({
          session,
          category: prompt.category,
          groupId: null,
          parentId: promptId,
          body,
        });
        posted = true;
      }
    }
  }

  let message;
  if (status === "attending") {
    message = posted
      ? "You’re on the attending list — see Room threads below."
      : prev === "attending"
        ? "You already marked that you’ll attend."
        : "Marked: I’ll attend.";
  } else {
    message = posted
      ? "Interest noted — see Room threads below."
      : prev === "interested"
        ? "You already marked interest."
        : "Marked: interested.";
  }

  return { status, posted, message, scrollTarget };
}

export async function shareHubListing(item) {
  ensureTheHub();
  if (!item?.id) return { mode: "failed", message: "Nothing to share." };
  bumpEventStat(item.id, "shares");
  const titleLink = resolveListingTitleLink(item);
  const when = [formatHubListingDate(item.date), item.time, item.location].filter(Boolean).join(" · ");
  const lines = [
    item.title,
    when,
    item.address,
    item.phone ? `Phone: ${item.phone}` : "",
    item.contact ? `Contact: ${item.contact}` : "",
    titleLink.href || "",
  ].filter(Boolean);
  const text = lines.join("\n");
  if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
    try {
      await navigator.share({
        title: item.title,
        text,
        url: titleLink.href || undefined,
      });
      return { mode: "shared", message: "Shared." };
    } catch (err) {
      if (err?.name === "AbortError") return { mode: "cancelled", message: "" };
    }
  }
  try {
    await navigator.clipboard.writeText(text);
    return { mode: "copied", message: "Event details copied — paste into email or text." };
  } catch {
    return { mode: "failed", message: "Could not copy. Select the listing text manually." };
  }
}

export function searchTheHub(query) {
  ensureTheHub();
  const q = String(query || "").trim().toLowerCase();
  if (!q) return { posts: [], events: [], categories: [] };
  const categories = listAllHubRooms().filter(
    (item) =>
      !item.archived &&
      (item.label.toLowerCase().includes(q) || item.id.includes(q) || (item.blurb || "").toLowerCase().includes(q))
  );
  const posts = read(KEYS.posts, []).filter(
    (row) => !row.hidden && `${row.body} ${row.author_name}`.toLowerCase().includes(q)
  );
  const events = read(KEYS.events, []).filter(
    (row) =>
      !row.hidden &&
      `${row.title} ${row.location} ${row.address} ${row.phone} ${row.contact} ${row.description} ${row.host}`
        .toLowerCase()
        .includes(q)
  );
  const groups = read(KEYS.groups, []).filter((row) => `${row.name} ${row.summary}`.toLowerCase().includes(q));
  return { posts, events, categories, groups };
}

export function roomKey({ category, groupId }) {
  return groupId ? `group:${groupId}` : `cat:${category}`;
}
